
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import CalendarNavigator from '@/components/menu/rotational-menus/CalendarNavigator.vue';

const namespace: string = 'restaurant';

@Component<WeekNavigator>({
	components: {
		CalendarNavigator
	}
})
export default class WeekNavigator extends Vue {
	@Action('setDateRange', { namespace }) private setDateRange!: (dateRange: DateRange) => Promise<void>;
	@Getter('getDateRange', { namespace }) private dateRange!: DateRange;
	@Getter('getZone', { namespace }) private zone!: string;
	@Getter('getOrderCutoffDate', { namespace }) private orderCutoffDate!: DateTime | null;
	@Getter('getMaxOrderDate', { namespace }) private maxOrderDate!: DateTime | null;
	@Prop({ type: Number, required: false, default: 0 }) private screenWidth!: number;

	private get dateString(): string | void {
		if (this.dateRange?.start) {
			const date = DateTime.fromISO(this.dateRange.start).setLocale(this.$i18n.locale).toFormat(`${this.screenWidth < 768 ? this.$t('menu.week_of_abbr_date_format') : 'DD'}`);
			return this.$t(this.screenWidth < 768 ? 'menu.week_of_abbr' : 'menu.week_of', { date });
		}
	}

	/**
	 * Compare selected date with the cutoff date to determine if the previous button should be disabled
	 * If there is no cutoff compare with the current date
	 *
	 * @return {boolean}
	 */
	private get isPrevDisabled(): boolean {
		if (!this.dateRange.start) {
			return false;
		}
		const startOfWeek = DateTime.fromISO(this.dateRange.start, { zone: this.zone }).startOf('week');
		if (this.orderCutoffDate) {
			return startOfWeek <= this.orderCutoffDate.startOf('week');
		}
		return startOfWeek <= DateTime.now().setZone(this.zone).startOf('week');
	}

	/**
	 * Compare the start of next week with the advanced order limit date to determine if the next button should be disabled
	 *
	 * @return {boolean}
	 */
	private get isNextDisabled(): boolean {
		if (!this.dateRange.start) {
			return false;
		}

		// If there is no advanced order limit, the button should not be disabled
		if (!this.maxOrderDate) {
			return false;
		}
		// Check if the start of the next week is after the max order date, if it is disable the button, since there is no valid ordering window
		return DateTime.fromISO(this.dateRange.start, { zone: this.zone }).plus({ week: 1 }).startOf('week') > this.maxOrderDate;
	}

	/**
	 * Change the date range to the next or previous or week and update the query params
	 *
	 * @param {string} direction
	 * @return {void}
	 */
	private async changeDateRange(direction: 'prev' | 'next'): Promise<void> {
		if (this.dateRange.start === null) {
			return;
		}
		const start = DateTime.fromISO(this.dateRange.start).plus({ weeks: direction === 'prev' ? -1 : 1 });

		await this.setDateRange({
			start: start.startOf('week').toISODate(),
			end: start.endOf('week').toISODate()
		});
		this.$router.replace({ query: { ...this.$route.query, selectedWeek: this.dateRange.start } }).catch(() => {});
	}
}
